import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useCart } from '../context/CartContext';
import { getProductImageObjectPosition } from '../data/products';

export default function Cart() {
  const { items, updateQuantity, removeFromCart, getCartTotal } = useCart();

  const subtotal = getCartTotal();
  const total = subtotal;

  if (items.length === 0) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-zinga">
            <div className="max-w-md mx-auto text-center py-16">
              <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                <ShoppingBag className="w-10 h-10 text-muted-foreground" />
              </div>
              <h1 className="font-display text-2xl font-bold mb-4">A kosarad üres</h1>
              <p className="text-muted-foreground mb-8">
                Úgy tűnik, még nem tettél semmit a kosaradba.
              </p>
              <Link to="/shop" className="btn-primary inline-flex items-center gap-2">
                Vásárlás megkezdése
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const categoryLabel = (category) => {
    switch (category) {
      case 'foods':
      case 'drinks':
        return 'Élelmiszer';
      case 'household':
        return 'Háztartás';
      case 'bodycare':
        return 'Testápolás';
      default:
        return category;
    }
  };

  return (
    <Layout>
      <div className="section-padding">
        <div className="container-zinga">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Vásárlás folytatása
          </Link>

          <h1 className="font-display text-3xl md:text-4xl font-bold mb-8">Kosár</h1>

          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => (
                <div key={item.product.id} className="card-zinga flex gap-4 border-[#0B2F22]">
                  <Link
                    to={`/product/${item.product.id}`}
                    className="w-24 h-24 flex-shrink-0 bg-secondary/50 rounded-xl overflow-hidden"
                  >
                    <img
                      src={item.product.image}
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: getProductImageObjectPosition(item.product) }}
                    />
                  </Link>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <Link
                          to={`/product/${item.product.id}`}
                          className="font-display font-semibold hover:text-foreground/80 transition-colors"
                        >
                          {item.product.name}
                        </Link>
                        <p className="text-sm text-muted-foreground capitalize mt-1">
                          {categoryLabel(item.product.category)}
                        </p>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.product.id)}
                        className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                        aria-label="Tétel eltávolítása"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="w-8 h-8 rounded-lg border border-border flex items-center justify-center hover:bg-secondary transition-colors"
                          aria-label="Mennyiség csökkentése"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          className="w-8 h-8 rounded-lg border border-border flex items-center justify-center hover:bg-secondary transition-colors"
                          aria-label="Mennyiség növelése"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      <p className="font-display font-semibold">
                        {(item.product.price * item.quantity).toLocaleString('hu-HU')} Ft
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <div className="card-zinga sticky top-24 border-[#0B2F22]">
                <h2 className="font-display text-xl font-semibold mb-6">Rendelés összesítő</h2>

                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Részösszeg</span>
                    <span>{subtotal.toLocaleString('hu-HU')} Ft</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    A szállítási mód kiválasztása a pénztárnál történik (GLS, Packeta, személyes
                    átvétel).
                  </p>
                  <div className="pt-4 border-t border-border flex justify-between">
                    <span className="font-display font-semibold">Összesen</span>
                    <span className="font-display font-semibold text-lg">{total.toLocaleString('hu-HU')} Ft</span>
                  </div>
                </div>

                <Link to="/checkout" className="btn-primary w-full mt-6 flex items-center justify-center gap-2">
                  Tovább a pénztárhoz
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
